import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TextField, Autocomplete, MenuItem } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
// css
import "./UpdateProject.css";
import pdfimg from "../../../Assets/images/pdf_img.png";
import uploadicon from "../../../Assets/images/upload_icon.png";
import urllink from "../../../Assets/images/url.png"
// csutom imports
import { HandleApiError } from "../../../Utils/CommonErrors/HandleApiError";
import showToast from "../../../Utils/Toast/ToastNotification";
import {
  showLoader,
  hideLoader,
} from "../../../Redux/Slice/Loader/loaderSlice";
import projectManagementService from "../../../Services/projectManagementService/projectManagementService";
import userManagementService from "../../../Services/UserManagement/UserManagementService";
import { getDisplayFileName } from "../../../Utils/fileDisplayName";

const UpdateProject = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();

  const [states, setStates] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [coordinators, setCoordinators] = useState([]);
  const [initialValues, setInitialValues] = useState({
    projectId: id,
    projectTypeId: "",
    streetAddress: "",
    stateId: "",
    suburb: "",
    postCode: "",
    status: "",
    startDate: "",
    endDate: "",
    coordinators: "",
    planFileUrl: null,
    other_files: "",
    delete_other_file_id: [],
    client_name: "",
    client_email: "",
    document_urls: []
  });

  const token = useSelector(
    (state) => state.auth?.user?.data?.body?.data?.token
  );

  const validationSchema = Yup.object().shape({
    streetAddress: Yup.string().required("Project street address is required"),
    suburb: Yup.string().required("Project suburb is required"),
    stateId: Yup.string().required("Project state is required"),
    projectTypeId: Yup.string().required("Project type is required"),
    projectCoordinators: Yup.string().required(
      "Project coordinators are required"
    ),
    postCode: Yup.number()
      .typeError("Post Code must be a number")
      .required("Post Code is required"),
    startDate: Yup.date().required("Start date is required"),
    endDate: Yup.date()
      .nullable()
      .min(Yup.ref("startDate"), "End date must be after start date"),
    status: Yup.string(),
    planFileUrl: Yup.mixed().nullable(),
    client_email: Yup.string().email("Invalid email address").nullable(),
    document_urls: Yup.array()
      .of(Yup.string().url("Each document URL must be valid"))
      .nullable(),
  });

  useEffect(() => {
    const getProjectDetails = async () => {
      try {
        dispatch(showLoader());

        const response = await projectManagementService.getProject(id, token);
        const projectData = response?.data?.body?.data;

        const fetchedCoordinators = projectData.coordinators || [];
        const allDocumentUrl = Array.isArray(projectData.allDocumenetUrls)
          ? projectData.allDocumenetUrls
          : [projectData.allDocumenetUrls].filter(Boolean);
        // Extract user IDs from fetched coordinators
        const selectedUserIds = fetchedCoordinators.map(
          (user) => user.user_id || user.id
        );

        const otherFiles = projectData.other_files || [];
        // Set initial Formik values
        setInitialValues((prevValues) => ({
          ...prevValues,
          ...projectData,
          coordinators: fetchedCoordinators,
          projectCoordinators: selectedUserIds.join(","),
          projectPlan: projectData.projectFileUrl,
          other_files: otherFiles,
          document_urls: allDocumentUrl || [],

        }));

        setCoordinators(fetchedCoordinators);
      } catch (error) {
        await HandleApiError(error);
      } finally {
        dispatch(hideLoader());
      }
    };

    getProjectDetails();
  }, [id]);
  // Ensure dependencies are correctly set

  useEffect(() => {
    const getStates = async () => {
      try {
        const response = await projectManagementService.getStates(token);
        if (response?.data?.statusCode === 200) {
          setStates(response?.data?.body?.data?.states);
        }
      } catch (error) {
        await HandleApiError(error);
      }
    };

    const getProjectTypes = async () => {
      try {
        const response = await projectManagementService.getProjectTypes(token);
        if (response?.data?.statusCode === 200) {
          setProjectTypes(response?.data?.body?.data?.states);
        }
      } catch (error) {
        await HandleApiError(error);
      }
    };

    const getUser = async () => {
      try {
        const params = {
          search: searchQuery || "",
          limit: 100,
        };
        const response = await userManagementService.getUserList(params, token);
        if (response?.data?.statusCode === 200) {
          setUsers(response.data.body.data.users);
        } else {
          showToast("error", response.data.body.message);
        }
      } catch (error) {
        await HandleApiError(error);
      }
    };
    getStates();
    getProjectTypes();
    getUser();
  }, []);

  const onSubmit = async (values) => {

    const isArchive = values.status === "Archive" ? true : false;
    const formData = new FormData();
    const fieldMapping = {
      id: "projectId",
      streetAddress: "streetAddress",
      suburb: "suburb",
      stateId: "state",
      projectTypeId: "projectType",
      coordinators: "user_id_array", // Backend expects a string here
      postCode: "postCode",
      startDate: "startDate",
      endDate: "endDate",
      status: "status",
      projectFileUrl: "planFileUrl",
      other_files: "new_other_files",
      client_name: "client_name",
      client_email: "client_email",
      is_archive: "is_archive",
      document_urls: "document_urls"
    };

    const existingFileIds = [];
    const newFiles = [];

    // ✅ Fix for `coordinators` - Convert array of objects to a comma-separated string
    let formattedCoordinators = "";
    if (Array.isArray(values.coordinators)) {
      formattedCoordinators = values.coordinators
        .map((user) => (user.user_id || user.id).replace(/"/g, "")) // Remove extra quotes
        .join(",");
    }

    values.document_urls.forEach((url) => {
      formData.append("document_urls[]", url);
    });

    values.other_files.forEach((file) => {
      if (file.id) {
        existingFileIds.push(file.id);
      } else {
        newFiles.push(file);
      }
    });

    Object.entries(values).forEach(([key, value]) => {
      const backendKey = fieldMapping[key] || key;

      if (key === "coordinators") {
        formData.append(backendKey, formattedCoordinators); // ✅ Use formatted coordinators

      } else if (key === "other_files") {
        if (values.delete_other_file_id?.length > 0) {
          formData.append(
            "delete_other_file_id",
            values.delete_other_file_id.join(",")
          );
        }

        newFiles.forEach((file) => {
          formData.append("new_other_files", file);
        });
      } else if (key === "delete_other_file_id") {
        return;
      } else if (value instanceof File) {
        formData.append(backendKey, value);
      } else if (
        Array.isArray(value) &&
        value.every((val) => val instanceof File)
      ) {
        value.forEach((file) => {
          formData.append(backendKey, file);
        });
      } else if (backendKey === 'is_archive') {
        formData.append("is_archive", isArchive);
      }
      else if (value !== null && value !== undefined) {
        formData.append(backendKey, value.toString());
      }


    });
    
    dispatch(showLoader());
    try {
      const response = await projectManagementService.updateProject(formData, token);
      if (response.data.statusCode === 200) {
        showToast("success", "Project Updated successfully");
        navigate("/project-listing");
      }
    } catch (error) {
      await HandleApiError(error);
    } finally {
      dispatch(hideLoader());
    }
  };

  return (
    <>
      <div className="main-inner-block">
        <div className="heading-content">Edit Project</div>
        <div className="content">
          <div className="white-box">
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({
                values,
                setFieldValue,
                handleChange,
                handleBlur,
                handleSubmit,
                errors,
                touched,
              }) => (
                <Form onSubmit={handleSubmit}>
                  <div class="create-user-block">
                    <div className="add-project-area">

                      {/* project details section */}
                      <div className="add-project-inner">
                        <h6 className="heading-content m-0 heading-text">Project Details</h6>
                        <div className="project-details-section row">
                          <div className="add-project-box col-md-4 mb-3">
                            <label htmlFor="projectTypeId" className="form-label">
                              Project Type<span>*</span>
                            </label>
                            <select
                              className="form-select"
                              name="projectTypeId"
                              id="projectTypeId"
                              value={values.projectTypeId}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              {/* <option disabled>Select Type</option> */}
                              {Array.isArray(projectTypes) &&
                                projectTypes.length > 0 ? (
                                projectTypes.map((projectTyepe_id) => (
                                  <option
                                    key={projectTyepe_id.id}
                                    value={projectTyepe_id.id}
                                  >
                                    {projectTyepe_id.name}
                                  </option>
                                ))
                              ) : (
                                <option disabled>No project types available</option>
                              )}
                            </select>
                            {errors.projectTypeId && touched.projectTypeId && (
                              <div className="error">{errors.projectTypeId}</div>
                            )}
                          </div>
                          <div className="add-project-box col-md-4 mb-3">
                            <label
                              htmlFor="projectCoordinators"
                              className="form-label"
                            >
                              Project Coordinators<span>*</span>
                            </label>
                            <Autocomplete
                              className="autocomplete-box position-relative"
                              sx={{
                                m: 1,
                                // width: 375,
                                "& .MuiOutlinedInput-root": {
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderBottom: "1px solid #e6e6e6 !important", // Remove the default border
                                  },
                                  "&:before, &:after": {
                                    borderBottom: "1px solid #e6e6e6 !important", // Ensure it applies
                                  },
                                  "&.Mui-focused:after": {
                                    borderBottom: "1px solid #e6e6e6 !important", // Override focus blue border
                                  },
                                },
                              }}
                              multiple
                              options={users} // Available coordinators list
                              getOptionLabel={(option) =>
                                `${option.first_name} ${option.last_name}`
                              }
                              disableCloseOnSelect
                              value={
                                Array.isArray(values.coordinators)
                                  ? values.coordinators
                                  : []
                              } // Ensure it's always an array
                              onChange={(event, newValue) => {
                               
                                // Make sure newValue is an array before calling .map
                                if (!Array.isArray(newValue)) {
                                  newValue = [];
                                }

                                // Extract user IDs as strings and ensure they don't have extra quotes
                                const selectedUserIds = newValue.map((user) =>
                                  (user.user_id || user.id).replace(/"/g, "")
                                );
                                // Set coordinators as an array (Formik requires an array for UI)
                                setFieldValue("coordinators", newValue);

                                // Set projectCoordinators as a string for backend (comma-separated)
                                setFieldValue(
                                  "projectCoordinators",
                                  selectedUserIds.join(",")
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="outlined"
                                  // placeholder="Co-ordinator"
                                  className="custom-input-box"
                                />
                              )}
                              renderOption={(props, option, { selected }) => (
                                <MenuItem
                                  {...props}
                                  key={option.user_id || option.id}
                                  sx={{ justifyContent: "space-between" }}
                                >
                                  {`${option.first_name} ${option.last_name}`}
                                  {selected ? <CheckIcon color="info" /> : null}
                                </MenuItem>
                              )}
                            />

                            {errors.projectCoordinators &&
                              touched.projectCoordinators && (
                                <div className="error">
                                  {errors.projectCoordinators}
                                </div>
                              )}
                          </div>
                          <div className="add-project-box col-md-4 mb-3">
                            <label htmlFor="startDate" className="form-label">
                              Start Date<span>*</span>
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              id="startDate"
                              name="startDate"
                              value={
                                values.startDate
                                  ? values.startDate.split("T")[0]
                                  : ""
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {errors.startDate && touched.startDate && (
                              <div className="error">{errors.startDate}</div>
                            )}
                          </div>
                          <div className="add-project-box col-md-4 mb-3">
                            <label htmlFor="endDate" className="form-label">
                              End Date<span>*</span>
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              id="endDate"
                              name="endDate"
                              value={
                                values.endDate ? values.endDate.split("T")[0] : ""
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {errors.endDate && touched.endDate && (
                              <div className="error">{errors.endDate}</div>
                            )}
                          </div>
                          <div className="add-project-box col-md-4 mb-3">
                            <label htmlFor="status" className="form-label">
                              Project Status<span>*</span>
                            </label>
                            <select
                              className="form-select"
                              id="status"
                              name="status"
                              value={values.status}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              {/* <option value="" disabled>Select Status</option> */}
                              <option selected value="New">
                                New
                              </option>
                              <option value="Active">Active</option>
                              <option value="Complete">Complete</option>
                              <option value="Inactive">Inactive</option>
                              <option value="On Hold">On Hold</option>
                              <option value="Archive">Archive</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* project address section */}
                      <div className="add-project-inner">
                        <h6 className="heading-content m-0 heading-text">Address</h6>
                        <div className="add-project-box">
                          <label htmlFor="streetAddress" className="form-label">
                            Project Street Address<span>*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="streetAddress"
                            id="streetAddress"
                            placeholder="Enter project address"
                            value={values.streetAddress}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {errors.streetAddress && touched.streetAddress && (
                            <div className="error">{errors.streetAddress}</div>
                          )}
                        </div>

                        <div className="address-section d-flex flex-wrap justify-content-between">
                          <div className="add-project-box">
                            <label htmlFor="suburb" className="form-label">
                              Project Suburb<span>*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              name="suburb"
                              id="suburb"
                              placeholder="Enter project address"
                              value={values.suburb}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {errors.suburb && touched.suburb && (
                              <div className="error">{errors.suburb}</div>
                            )}
                          </div>

                          <div className="add-project-box">
                            <label htmlFor="stateId" className="form-label">
                              Project State<span>*</span>
                            </label>
                            <select
                              className="form-select"
                              id="stateId"
                              name="stateId"
                              value={values.stateId}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              {/* <option disabled>Select Type</option> */}
                              {Array.isArray(states) && states.length > 0 ? (
                                states.map((state_id) => (
                                  <option key={state_id.id} value={state_id.id}>
                                    {state_id.name}
                                  </option>
                                ))
                              ) : (
                                <option disabled>No project types available</option>
                              )}
                            </select>
                            {errors.stateId && touched.stateId && (
                              <div className="error">{errors.stateId}</div>
                            )}
                          </div>

                          <div className="add-project-box">
                            <label htmlFor="postCode" className="form-label">
                              Post Code<span>*</span>
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              id="postCode"
                              placeholder="Enter Post Code"
                              name="postCode"
                              value={values.postCode}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {errors.postCode && touched.postCode && (
                              <div className="error">{errors.postCode}</div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* relevant document */}
                      <div className="add-project-inner">
                        <h6 className="heading-content m-0 heading-text">Relevant Documents</h6>
                        <div className="relevant-section row">
                          <div className="col-xl-6 relevant-section">
                            <div className="add-project-box project-plan-box">
                              <label htmlFor="planFileUrl" className="form-label">
                                Project Plan<span>*</span>
                              </label>

                              <div class="file-upload upload-file-block">
                                <img
                                  src={uploadicon}
                                  alt="upload-icon"
                                  className="me-2 ms-2"
                                ></img>
                                <img src={pdfimg} class="file-icon" />
                                <span className="flex-grow-1 text-truncate">
                                  <span
                                    className="file-name text-truncate"
                                    title={getDisplayFileName(values.planFileUrl) || "No file selected"}
                                  >
                                    {values.planFileUrl
                                      ? getDisplayFileName(values.planFileUrl)
                                      : "No file selected"}
                                  </span>

                                </span>
                                {values.planFileUrl && (
                                  <span
                                    className="remove-btn ms-2"
                                    onClick={() =>
                                      setFieldValue("planFileUrl", null)
                                    }
                                  >
                                    &#x2715;
                                  </span>
                                )}


                                <label class="upload-btn">
                                  Upload File
                                  <input
                                    type="file"
                                    name="planFileUrl"
                                    onClick={(e) => (e.target.value = null)}
                                    onChange={(e) => {
                                      const file = e.target.files[0];
                                      if (file) {
                                        setFieldValue("planFileUrl", file);
                                      }
                                    }}
                                    accept=".pdf"
                                  />
                                </label>
                              </div>
                              {errors.planFileUrl && touched.planFileUrl && (
                                <div className="error">{errors.planFileUrl}</div>
                              )}
                            </div>
                          </div>

                          <div className="col-xl-6 relevant-section">
                            <div className="add-project-box project-plan-box">
                              <label htmlFor="other_files" className="form-label">
                                Other Files
                              </label>

                              <div className="file-upload upload-file-block">
                                <img
                                  src={uploadicon}
                                  alt="upload-icon"
                                  className="me-2 ms-2"
                                />
                                <img src={pdfimg} className="file-icon" />
                                <span className="flex-grow-1 file-name text-truncate">
                                  {Array.isArray(values.other_files) &&
                                    values.other_files.length > 0
                                    ? values.other_files.map((file, index) => {
                                      const fileName = getDisplayFileName(file);

                                      return (

                                        <div key={index} className="file-namen d-flex align-items-center">
                                          <span className="text-truncate" title={fileName} style={{ maxWidth: '200px' }}>
                                            {/* {fileName.length > 30 ? `${fileName.slice(0, 20)}...` : fileName} */}
                                            {fileName}
                                          </span>
                                          <span
                                            className="remove-btn ms-2"
                                            onClick={() => {
                                              const removedFile = values.other_files[index];

                                              if (removedFile?.id) {
                                                setFieldValue("delete_other_file_id", [
                                                  ...(values.delete_other_file_id || []),
                                                  removedFile.id,
                                                ]);
                                              }

                                              const updatedFiles = values.other_files.filter((_, i) => i !== index);
                                              setFieldValue("other_files", updatedFiles);
                                            }}
                                          >
                                            &#x2715;
                                          </span>
                                        </div>

                                      );
                                    })
                                    : "No files selected"}
                                </span>

                                <label className="upload-btn">
                                  Upload Files
                                  <input
                                    type="file"
                                    multiple
                                    name="other_files"
                                    onChange={(e) => {
                                      const newFiles = Array.from(e.target.files);
                                      setFieldValue("other_files", [
                                        ...(values.other_files || []),
                                        ...newFiles,
                                      ]);
                                    }}
                                    accept=".pdf"
                                  />
                                </label>
                              </div>
                            </div>
                          </div>
                          {/* Document URLs Section */}

                        </div>

                        <div className="add-project-box add-docs-url">
                          <label htmlFor="document_urls" className="form-label">
                            All Documents URLs
                          </label>
                          <div className="urlsClass">
                            {values.document_urls.map((url, index) => (
                              <div key={index} className="d-flex align-items-center mb-2">
                                <span className="me-2">
                                  <img src={urllink} alt="project-plan" className="link-class"></img>
                                </span>
                                <a href={url} target="_blank" rel="noopener noreferrer" className="document-url">
                                  {url}
                                </a>
                                <span
                                  className="remove-btn ms-2"
                                  onClick={() => {
                                    // Remove the URL from the array
                                    const updatedUrls = values.document_urls.filter(
                                      (_, i) => i !== index
                                    );
                                    setFieldValue("document_urls", updatedUrls);
                                  }}
                                >
                                  &#x2715; {/* Close icon to remove */}
                                </span>
                              </div>
                            ))}
                          </div>

                          <input
                            type="text"
                            className="form-control"
                            id="document_urls"
                            name="newDocumentUrl"
                            value={values.newDocumentUrl || ""}
                            placeholder="Add Document URLs"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && values.newDocumentUrl) {
                                e.preventDefault();

                                setFieldValue("document_urls", [
                                  ...values.document_urls,
                                  values.newDocumentUrl,
                                ]);

                                setFieldValue("newDocumentUrl", "");
                              }
                            }}
                          />

                          {errors.document_urls && touched.document_urls && (
                            <div className="error">{errors.document_urls}</div>
                          )}
                        </div>

                      </div>


                      {/* client details */}
                      <div className="add-project-inner">
                        <h6 className="heading-content m-0 heading-text">Client Details</h6>
                        <div className="row justify-content-between">
                          <div className="col-md-6 mb-4">
                            <label htmlFor="client_name" className="form-label">
                              Client Name
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="client_name"
                              placeholder="Enter Client Name"
                              name="client_name"
                              value={values.client_name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />

                          </div>

                          <div className="col-md-6">
                            <label htmlFor="client_email" className="form-label">
                              Client Email
                            </label>
                            <input
                              type="email"
                              className="form-control"
                              id="client_email"
                              placeholder="Enter Client Email"
                              name="client_email"
                              value={values.client_email}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {errors.client_email && touched.client_email && (
                              <div className="error">{errors.client_email}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="text-center update-div d-flex align-items-center justify-content-center gap-2 mt-3">
                    <button
                      type="button"
                      class="green-btn submit-btn"
                      onClick={() => navigate(-1)}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      class="green-btn submit-btn"
                    >
                      Update
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </>
  );
};

export default UpdateProject;
